import React, { useMemo } from 'react'
import type { Control, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Card from '@/components/Card'
import Select from '@/components/Select'
import {
  FORM_FIELDS,
  type FormData,
} from '@/pages/getRecipe/allInOne/utils/formTypes'
import type { RootState } from '@/store'
import { updateAllInOneFormValue } from '@/store/allInOne'
import { useGetMetadataQuery } from '@/store/apiService'
import { REGION } from '@/utils/constants'

import {
  COLOR_MEASUREMENT_TEXT,
  COLOR_VALUE,
  DENTIN_TYPE,
  PREP_DENTIN_DROPDOWN_OPTIONS,
  PREP_DROPDOWN_OPTIONS,
  getOptions,
} from '../utils/constants'
import ColorSelections from './components/ColorSelections'
import ColorSelects from './components/ColorSelects'

interface Metadata {
  available_indications: string[]
  available_vita_codes: string[]
  available_vita_classic_codes: string[]
  available_ndcard_options: string[]
  [key: string]: any
}

interface MeasureColorProps {
  control: Control<FormData>
  recipeTypeValue: string
  setValue: UseFormSetValue<FormData>
  clearErrors: (name?: keyof FormData | (keyof FormData)[]) => void
  errors: FieldErrors<FormData>
  requiredInputValidation: any
  containerRef?: React.RefObject<HTMLDivElement>
}

const MeasureColor: React.FC<MeasureColorProps> = ({
  control,
  recipeTypeValue,
  setValue,
  clearErrors,
  errors,
  requiredInputValidation,
  containerRef,
}) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const prepColor =
    useSelector(
      (state: RootState) => state.allInOne.form[FORM_FIELDS.PREP_COLOR],
    ) || COLOR_VALUE.LabColor
  const dentinColor =
    useSelector(
      (state: RootState) => state.allInOne.form[FORM_FIELDS.DENTIN_COLOR],
    ) || COLOR_VALUE.LabColor

  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as { data: Metadata | undefined; error: any; isLoading: boolean }

  const skinEnamelTypes = useMemo(
    () => (metadata ? getOptions(metadata, 'available_skinenameltypes') : []),
    [metadata],
  )

  const vitaOptions = useMemo(
    () => (metadata ? getOptions(metadata, 'available_vita_codes') : []),
    [metadata],
  )

  const vitaClassicOptions = useMemo(
    () =>
      metadata ? getOptions(metadata, 'available_vita_classic_codes') : [],
    [metadata],
  )

  const ndCardOptions = useMemo(
    () => (metadata ? getOptions(metadata, 'available_ndcard_options') : []),
    [metadata],
  )

  const isTripleDentin = recipeTypeValue === DENTIN_TYPE.TRIPLE_DENTIN

  const clearFormValues = (prefix: string, newValue: string) => {
    const isPrep = prefix === 'prep'
    const basePrefix = isPrep ? 'prepcolor_' : 'target'
    const regions = Object.values(REGION)
    let value = '' as any
    if (newValue === COLOR_VALUE.LabColor) {
      value = [null, null, null]
    }
    regions.forEach((region: REGION) => {
      const fieldName = `${basePrefix}${t(region).toLowerCase()}`
      dispatch(updateAllInOneFormValue({ name: fieldName as any, value }))
      setValue(fieldName as any, value)
      clearErrors(fieldName as keyof FormData | (keyof FormData)[])
    })
  }

  const getCurrentOptions = (
    selectedColor: string,
    includeNaturalDie = false,
  ) => {
    if (selectedColor === COLOR_VALUE.Vita3DMaster) {
      return vitaOptions
    } else if (selectedColor === COLOR_VALUE.VitaClassic) {
      return vitaClassicOptions
    } else if (
      includeNaturalDie &&
      selectedColor === COLOR_VALUE.NaturalDieMaterial
    ) {
      return ndCardOptions
    }
    return []
  }

  return (
    <>
      {requiredInputValidation[FORM_FIELDS.PREP_COLOR] && (
        <Card>
          <Select
            options={PREP_DROPDOWN_OPTIONS}
            title={t(COLOR_MEASUREMENT_TEXT.preparationColor.title)}
            tooltipText={t(COLOR_MEASUREMENT_TEXT.preparationColor.tooltip)}
            control={control}
            name={FORM_FIELDS.PREP_COLOR}
            onFormValueChange={(name, value) => {
              dispatch(updateAllInOneFormValue({ name, value }))
              clearFormValues('prep', value)
            }}
            direction="row"
          />

          {prepColor === COLOR_VALUE.LabColor && (
            <ColorSelections
              control={control}
              prefix="prep"
              errors={errors}
              clearErrors={clearErrors}
              setValue={setValue}
              isTripleDentin={isTripleDentin}
              containerRef={containerRef}
            />
          )}
          <ColorSelects
            control={control}
            prefix="prep"
            selectedColor={prepColor}
            currentOptions={getCurrentOptions(prepColor, true)}
            isTripleDentin={isTripleDentin}
            dispatch={dispatch}
          />
        </Card>
      )}
      {requiredInputValidation[FORM_FIELDS.DENTIN_COLOR] && (
        <Card>
          <Select
            options={PREP_DENTIN_DROPDOWN_OPTIONS}
            title={t(COLOR_MEASUREMENT_TEXT.dentinColor.title)}
            tooltipText={t(COLOR_MEASUREMENT_TEXT.dentinColor.tooltip)}
            control={control}
            name={FORM_FIELDS.DENTIN_COLOR}
            onFormValueChange={(name, value) => {
              dispatch(updateAllInOneFormValue({ name, value }))
              clearFormValues('dentin', value)
            }}
            direction="row"
          />
          {dentinColor === COLOR_VALUE.LabColor && (
            <ColorSelections
              control={control}
              prefix="dentin"
              errors={errors}
              clearErrors={clearErrors}
              setValue={setValue}
              isTripleDentin={isTripleDentin}
              containerRef={containerRef}
            />
          )}
          <ColorSelects
            control={control}
            prefix="dentin"
            selectedColor={dentinColor}
            currentOptions={getCurrentOptions(dentinColor)}
            isTripleDentin={isTripleDentin}
            dispatch={dispatch}
          />
        </Card>
      )}
      <Card>
        <Select
          options={skinEnamelTypes}
          title={t(COLOR_MEASUREMENT_TEXT.skinEnamelType.title)}
          tooltipText={t(COLOR_MEASUREMENT_TEXT.skinEnamelType.tooltip)}
          control={control}
          name={FORM_FIELDS.SKINENAMELTYPE}
          onFormValueChange={(name, value) =>
            dispatch(updateAllInOneFormValue({ name, value }))
          }
        />
      </Card>
    </>
  )
}

export default MeasureColor
